/**
 * Source-bar + recognized-layout-banner composition for MappingPhase
 * (column-mapping-ui spec: "Header auto-detected and conditional sheet
 * picker", "Auto-suggest banner for a recognized layout"). Consolidated to a
 * SINGLE banner: the recognized-layout banner replaces the legacy
 * "saved mapping applied" variant of MappingStatusBanner — the non-standard
 * fallback banner is unaffected.
 */
import { formatTemplate } from '@ooh/ui/lib/utils';
import { MappingStatusBanner } from './mapping-phase-parts';
import {
  MappingTransformsAutoSuggestBanner,
  type MappingTransformsAutoSuggestBannerLabels,
} from './mapping-transforms-auto-suggest-banner';
import {
  MappingTransformsSourceBar,
  type MappingTransformsSourceBarLabels,
} from './mapping-transforms-source-bar';
import type { MappingPhaseLabels } from './mapping-phase';

export function buildAutoSuggestBannerLabels(
  labels: MappingPhaseLabels,
): MappingTransformsAutoSuggestBannerLabels {
  return {
    recognizedText: labels.recognizedLayoutText,
    infoLabel: labels.recognizedLayoutInfoLabel,
    infoText: labels.recognizedLayoutInfoText,
    clearActionLabel: labels.recognizedLayoutClearLabel,
  };
}

export function buildSourceBarLabels(
  labels: MappingPhaseLabels,
  headerRowIndex: number | undefined,
): MappingTransformsSourceBarLabels {
  return {
    fileSummaryTemplate: labels.fileSummaryTemplate,
    fileSummaryTemplateRowsOnly: labels.fileSummaryTemplateRowsOnly,
    sheetPickerLabel: labels.sheetPickerLabel,
    headerAutoNote: formatTemplate(labels.headerAutoNoteTemplate, {
      row: (headerRowIndex ?? 0) + 1,
    }),
    headerAutoInfoLabel: labels.headerAutoInfoLabel,
    headerAutoInfoText: labels.headerAutoInfoText,
  };
}

export interface MappingPhaseSourceSectionProps {
  hasSavedMapping: boolean;
  savedConfigCleared: boolean;
  onClearSavedConfig: () => void;
  nonStandardBanner: string;
  autoSuggestLabels: MappingTransformsAutoSuggestBannerLabels;
  fileName?: string;
  fileSizeKb?: number;
  estimatedRows?: number;
  sheets?: string[];
  selectedSheet?: string;
  onSheetChange?: (sheet: string) => void;
  sourceBarLabels: MappingTransformsSourceBarLabels;
}

function noopSheetChange(): void {
  /* no-op default when the parent doesn't support sheet switching */
}

function RecognizedLayoutOrFallbackBanner({
  hasSavedMapping,
  savedConfigCleared,
  onClearSavedConfig,
  nonStandardBanner,
  autoSuggestLabels,
}: Pick<
  MappingPhaseSourceSectionProps,
  | 'hasSavedMapping'
  | 'savedConfigCleared'
  | 'onClearSavedConfig'
  | 'nonStandardBanner'
  | 'autoSuggestLabels'
>) {
  if (hasSavedMapping && !savedConfigCleared) {
    return (
      <MappingTransformsAutoSuggestBanner labels={autoSuggestLabels} onClear={onClearSavedConfig} />
    );
  }
  if (!hasSavedMapping) {
    return <MappingStatusBanner nonStandardBanner={nonStandardBanner} />;
  }
  // Saved config was recognized then explicitly cleared — banner is dismissed entirely
  // (column-mapping-ui spec: "Clearing the config reverts to manual mapping").
  return null;
}

export function MappingPhaseSourceSection({
  hasSavedMapping,
  savedConfigCleared,
  onClearSavedConfig,
  nonStandardBanner,
  autoSuggestLabels,
  fileName,
  fileSizeKb,
  estimatedRows,
  sheets,
  selectedSheet,
  onSheetChange,
  sourceBarLabels,
}: MappingPhaseSourceSectionProps) {
  const resolvedSheets = sheets ?? [];
  return (
    <>
      <MappingTransformsSourceBar
        fileName={fileName ?? ''}
        fileSizeKb={fileSizeKb}
        estimatedRows={estimatedRows ?? 0}
        sheets={resolvedSheets}
        selectedSheet={selectedSheet ?? resolvedSheets[0] ?? ''}
        onSheetChange={onSheetChange ?? noopSheetChange}
        labels={sourceBarLabels}
      />
      <RecognizedLayoutOrFallbackBanner
        hasSavedMapping={hasSavedMapping}
        savedConfigCleared={savedConfigCleared}
        onClearSavedConfig={onClearSavedConfig}
        nonStandardBanner={nonStandardBanner}
        autoSuggestLabels={autoSuggestLabels}
      />
    </>
  );
}
